import React, { useState, useContext, useEffect, useCallback } from 'react';
import AuthLogin from "../profile/auth-login";
import LoggedInHeader from '../components/LoggedInHeader';
import '../assets/styles/ReferFriend.css';

const ReferFriend = () => {
  const ctx = useContext(AuthLogin);
  
  const [referralEmail, setReferralEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', content: '' });
  const [referralCode, setReferralCode] = useState('');
  const [referrals, setReferrals] = useState([]);

  const fetchReferrals = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/referrals/${ctx.userId}`);
      const data = await response.json();
      
      if (response.ok) {
        setReferrals(data.referrals || []);
        
        // Set referral code if available
        if (data.referralCode) {
          setReferralCode(data.referralCode);
        }
      }
    } catch (error) {
      console.error('Error fetching referrals:', error);
    }
  }, [ctx.userId]);

  // Fetch existing referrals for the user
  useEffect(() => {
    if (ctx.isLoggedIn) {
      fetchReferrals();
    }
  }, [ctx.isLoggedIn, fetchReferrals]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!referralEmail || !referralEmail.includes('@')) {
      setMessage({ type: 'error', content: 'Please enter a valid email address' });
      return;
    }
    
    setLoading(true);
    setMessage({ type: '', content: '' });
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/create-referral`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          referrerId: ctx.userId,
          referralEmail,
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setMessage({ 
          type: 'success', 
          content: 'Referral invitation sent successfully!' 
        });
        setReferralEmail('');
        
        // Update referrals list
        fetchReferrals();
      } else {
        setMessage({ 
          type: 'error', 
          content: data.error || 'Failed to send referral invitation' 
        });
      }
    } catch (error) {
      console.error('Error sending referral:', error);
      setMessage({ 
        type: 'error', 
        content: 'An error occurred while sending the referral invitation' 
      });
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = () => {
    const referralLink = `${window.location.origin}/signup?ref=${referralCode}`;
    navigator.clipboard.writeText(referralLink);
    setMessage({ type: 'success', content: 'Referral link copied to clipboard!' });
  };

  // Format date string to readable format
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <>
      <LoggedInHeader />
      <div className="refer-friend-container">
        <div className="refer-friend-header">
          <h1>Refer a Friend</h1>
          <p className="refer-subtitle">
            Invite your friends to join FINNTECH and earn 1 month of free subscription when they sign up!
          </p>
        </div>
        
        <div className="refer-friend-content">
          <div className="refer-friend-left">
            <div className="referral-form-container">
              <h2>Send an Invitation</h2>
              <form onSubmit={handleSubmit} className="referral-form">
                <div className="form-group">
                  <label htmlFor="referral-email">Friend's Email Address</label>
                  <input
                    type="email"
                    id="referral-email"
                    value={referralEmail}
                    onChange={(e) => setReferralEmail(e.target.value)}
                    placeholder="Enter your friend's email"
                    disabled={loading}
                  />
                </div>
                
                <button 
                  type="submit" 
                  className="send-referral-btn"
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Invitation'}
                </button>
              </form>
              
              {message.content && (
                <div className={`message ${message.type}`}>
                  {message.content}
                </div>
              )}
            </div>
            
            {referralCode && (
              <div className="referral-link-container">
                <h2>Share Your Referral Link</h2>
                <div className="referral-link">
                  <input 
                    type="text" 
                    value={`${window.location.origin}/signup?ref=${referralCode}`}
                    readOnly
                  />
                  <button onClick={copyReferralLink}>Copy</button>
                </div>
              </div>
            )}
            
            {referrals.length > 0 && (
              <div className="referrals-history">
                <h2>Your Referrals</h2>
                <div className="referrals-list">
                  <table>
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Date</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {referrals.map((referral) => (
                        <tr key={referral.id}>
                          <td data-label="Email">{referral.referral_email}</td>
                          <td data-label="Date">{formatDate(referral.created_at)}</td>
                          <td data-label="Status">
                            <span className={`status-badge ${referral.status}`}>
                              {referral.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
          
          <div className="refer-friend-right">
            <div className="how-it-works">
              <h2>How It Works</h2>
              <div className="steps">
                <div className="step-refer">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <h3>Invite Your Friends</h3>
                    <p>Send invitations to your friends via email or share your unique referral link</p>
                  </div>
                </div>
                <div className="step-refer">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <h3>They Sign Up</h3>
                    <p>Your friends create an account and subscribe to FINNTECH using your referral</p>
                  </div>
                </div>
                <div className="step-refer">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <h3>Get Rewarded</h3>
                    <p>You receive 1 month of free subscription when they become a customer</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* <div className="referral-image">
              <img src={referralImage} alt="Refer a friend" />
            </div> */}
          </div>
        </div>
      </div>
    </>
  );
};

export default ReferFriend; 